<template>
  <section class="fiat-payout-creation-wrapper">
    <vs-popup
      title="Why is this required?"
      vs-lg="6"
      vs-md="6"
      :active.sync="isRequirementReasonDisplayed"
      class="purpose-popup"
      classContent="purpose-popup-content-wrapper">
      <p>As we are a regulated entity, these informations are needed for compliance reasons.</p>
      <div class="action-group">
        <button class="btn" @click="isRequirementReasonDisplayed = false">Ok</button>
      </div>
    </vs-popup>
    <h1 class="page-title">Create FIAT Payout</h1>
    <!-- Balances -->
    <Fiat-balance
      v-if="balancesGetter.length > 0"
      :balanceList="balancesGetter"
      title="Available Transfer Balance">
    </Fiat-balance>

    <button v-if="currentStepIndex > 0 && currentStepIndex < payoutCreationSteps.length" @click="backButtonHandler()" class="btn icon-btn back">{{ `Back to : ${payoutCreationSteps[currentStepIndex - 1]} ` }}</button>
    <h2 v-if="currentStepIndex < payoutCreationSteps.length">{{ `Step ${currentStepIndex + 1} of ${payoutCreationSteps.length}: ${payoutCreationSteps[currentStepIndex]}` }}</h2>

    <!-- ERRORS -->
    <Transition name="fade">
      <ul class="error-banner" v-if="errors.length > 0">
        <li v-for="error in errors" :key="error.code">{{ `${error.message}` }} {{ error.code && `(${error.code})` }}</li>
        <li v-if="!isInsufficientBalance">Please try again. If errors persists, please contact our <a href="mailto:support@triple-a.io">support team</a>.</li>
      </ul>
    </Transition>
    <Transition>
      <ul class="warning banner" v-if="missingInfosInAccounts.sender.length > 0 || missingInfosInAccounts.recipient.length > 0 || missingInfosInAccounts.other.length > 0">
        <li>Some informations are missing in the accounts you are trying to use : </li>
        <li v-if="missingInfosInAccounts.sender.length > 0">{{ `Sender is missing ${missingInfosInAccounts.sender.join(', ')}` }}. <button @click="openEditSender()" class="btn link-style">Add this information</button></li>
        <li v-if="missingInfosInAccounts.recipient.length > 0">{{ `Recipient is missing ${missingInfosInAccounts.recipient.join(', ')}` }}. <button @click="openEditReceiver()" class="btn link-style">Add this information</button></li>
        <li v-if="missingInfosInAccounts.other.length > 0">{{ `Account is missing ${missingInfosInAccounts.other.join(', ')}` }}</li>
        <li>Please add these informations via <router-link to="/manage-fiat-accounts">your manage-fiat-account page</router-link> or request help from our <a href="mailto:support@triple-a.io">support team</a>.</li>
      </ul>
    </Transition>

    <!-- Step 1 : Sender, recipient and amounts -->
    <section v-if="currentStepIndex === 0 && hasRights && !isMerchantConfigLoading" class="step1-wrapper">
      <form @submit.prevent="createQuote()">
        <p v-if="!isAllowedToChooseSender && senderAccount" class="account-wrapper">
          <span>From</span>
          {{ senderAccount.first_name }}
        </p>
        <fieldset class="account-wrapper" v-else>
          <div>
            <legend>From</legend>
          </div>
          <vs-select
            autocomplete
            v-model="senderAccount"
            placeholder="Sender Account"
            class="account"
            required
            width="100%"
            :disabled="!isAllowedToChooseSender"
            :danger="isSenderAccountInvalid"
            danger-text="Please select a sender account with a verified KYC."
            @change="handleSenderChange()">
            <vs-select-group title="Individuals" v-if="individualAccountsList.length > 0">
              <vs-select-item :key="index" :value="acc" :text="`${acc.first_name} ${acc.last_name} - ${acc.country_code} (KYC ${formatKycStatus(acc.verification_status)})`" v-for="(acc, index) of individualAccountsList" />
            </vs-select-group>
            <vs-select-group title="Corporates" v-if="companyAccountsList.length > 0">
              <vs-select-item :key="index" :value="acc" :text="`${acc.registered_name} - ${acc.country_code}`" v-for="(acc, index) of companyAccountsList" />
            </vs-select-group>
          </vs-select>
          <router-link to="/create-fiat-account/sender" v-if="isAllowedToChooseSender">Create new Sender</router-link>
        </fieldset>
        <Transition name="fade">
          <fieldset class="money" v-if="destinationAccount">
            <legend>Send (excluding fees):</legend>
            <vs-select autocomplete label="Currency"
              v-model="sendingBalance"
              required
              :danger="isSendingCurrencyInvalid"
              danger-text="Please select a valid currency.">
              <vs-select-item :key="index" :value="bal" :text="bal.currency" v-for="(bal, index) in balancesGetter" />
            </vs-select>
            <vs-input label="Amount"
              :placeholder="`${isQuoteLoading && transferMode === PAYOUT_MODES.RECEIVING ? 'Loading...' : '0.00'}`"
              v-model.trim="sendingAmount"
              type="number" min="0" :max="sendingBalance.available_balance"
              step="0.01" @input="searchInputHandler(true)" class="amount" :danger="isSendingAmountInvalid"
              :danger-text="`Please input a valid amount.`" />
            <div class="vs-con-loading__container" id="quote-loading-sending"></div>
            <small v-if="sendingBalance.currency">Available balance : {{ `${sendingBalance.type === 'fiat_money' ? formatFiatPrice(sendingBalance.available_balance) : formatCryptoAmount(sendingBalance.available_balance, sendingBalance.currency)} ${sendingBalance.currency}` }}</small>
            <p v-if="quote.fee_currency">{{ `Total to pay (including fees) = ${formatQuoteAmount(totalToPayGetter, quote.sending_currency)} ${quote.sending_currency}` }}</p>
            <!-- SWIFT fee -->
            <div class="fee-mode-wrapper" v-if="canDecideSwiftFeeMode">
              SWIFT fee
              <span class="tooltip">
                <img
                  src="../../assets/icons/help.svg"
                  alt="more info on reference ID" />
                <span class="tooltip-text">
                  <b>SHA</b>: Transaction fees are split between sender and beneficiary. The beneficiary might not receive the full amount.
                  <br>
                  <b>OUR</b>: The sender covers the Intermediary Bank (SWIFT) fee, so that the beneficiary receives the full amount.
                </span>
              </span>
              <ul>
                <li v-for="mode in feeModeOptions" :key="mode.value">
                  <input type="radio" v-model="feeMode" :value="mode.value" :id="`radio_${mode.value}`" name="fee-mode-radio">
                  <label :for="`radio_${mode.value}`">
                    {{ mode.display }}
                  </label>
                </li>
              </ul>
            </div>
          </fieldset>
        </Transition>
        <fieldset class="account-wrapper beneficiary">
          <div>
            <legend>To</legend>
          </div>
          <vs-select
            autocomplete
            v-model="destinationAccount"
            placeholder="Beneficiary Account"
            class="account"
            required
            width="100%"
            :danger="isDestinationAccountInvalid"
            danger-text="Please select a beneficiary account."
            @change="resetQuoteFormErrors()">
            <vs-select-item :key="index" :value="acc" :text="`${acc.account.account_name} - ${acc.account.account_number} (${acc.currency})`" v-for="(acc, index) of destinationAccountsList" />
          </vs-select>
          <router-link to="/create-fiat-account/beneficiary">Create new Beneficiary</router-link>
        </fieldset>
        <Transition name="fade">
          <fieldset class="money" v-if="destinationAccount">
            <legend>Beneficiary receives:</legend>
            <vs-input label="Currency" v-model.trim="receivingCurrency" disabled />
            <vs-input class="amount" label="Amount" :placeholder="`${isQuoteLoading && PAYOUT_MODES.SENDING ? 'Loading...' : '0.00'}`" v-model.trim="receivingAmount" type="number" min="0" step="0.01" @input="searchInputHandler(false)" :danger="isReceivingAmountInvalid" danger-text="Please input a valid amount." />
            <div class="vs-con-loading__container" id="quote-loading-receiving"></div>
          </fieldset>
        </Transition>
        <label class="collapse" v-if="quote.receiving_amount"> <!-- v-if="quote.id" -->
          How is this amount calculated ?
          <input type="checkbox" v-model.trim="shouldDisplayDetails" />
        </label>
      </form>
      <Transition name="slide-fade">
        <aside v-if="shouldDisplayDetails && quote.id">
          <dl>
            <div>
              <dt>Original amount</dt>
              <dd>{{ quote.sending_currency }}</dd>
              <dd>{{ formatQuoteAmount(quote.sending_amount, quote.sending_currency) }}</dd>
            </div>
            <div>
              <dt>Transaction Fee</dt>
              <dd>{{ quote.fee_currency }}</dd>
              <dd>{{ formatQuoteAmount(quote.fee - quote.intermediary_bank_fee, quote.fee_currency) }}</dd>
            </div>
            <div v-if="quote.intermediary_bank_fee">
              <dt>Intermediary Bank Fee</dt>
              <dd>{{ quote.fee_currency }}</dd>
              <dd>{{ formatQuoteAmount(quote.intermediary_bank_fee, quote.fee_currency) }}</dd>
            </div>
            <div>
              <dt>Amount to pay <br><small>(original amount + fees)</small></dt>
              <dd>{{ quote.sending_currency }}</dd>
              <dd>{{ formatQuoteAmount(totalToPayGetter, quote.sending_currency) }}</dd>
            </div>
            <div>
              <dt>Conversion rate</dt>
              <dd>{{ `${quote.sending_currency} 1.0000` }}</dd>
              <dd>{{ `${quote.receiving_currency} ${formatExchangeRateValue(quote.exchange_rate, 8)}` }}</dd>
            </div>
            <div>
              <dt>Amount received <br><small>(original amount x conversion rate)</small></dt>
              <dd>{{ quote.receiving_currency }}</dd>
              <dd>{{ formatFiatPrice(quote.receiving_amount) }}</dd>
            </div>
          </dl>
        </aside>
      </Transition>
    </section>

    <!-- Step 2 : Purpose -->
    <Transition name="fade">
      <section v-if="currentStepIndex === 1 && !isQuoteExpired" class="step2-wrapper">
        <label>Why is this required ?<input type="checkbox" v-model="isRequirementReasonDisplayed" /></label>
        <form v-on:submit.prevent="">
          <label>
            Purpose of Remittance <span class="req">*</span>
            <vs-select
              v-model="purpose"
              autocomplete
              placeholder="Select purpose"
              class="purpose"
              :danger="isPurposeInvalid"
              danger-text="Please select a purpose."
              v-on:change="isPurposeInvalid = false">
              <vs-select-item :key="index" :value="purpose" :text="purpose.display" v-for="(purpose, index) in purposeList" />
            </vs-select>
          </label>
          <label>
            Purpose of remittance document (ex: invoice) <span class="req" v-if="isPurposeDocRequired">*</span>
            <input type="file" accept="image/jpeg,application/pdf,image/png" name="file" @change="onPurposeFileChange" />
            <!-- to show the document is still attached when clicking on back button -->
            {{ purposeDocument ? purposeDocument.name : '' }}
            <span class="error" v-if="isPurposeDocInvalid">Please upload a valid supporting document (max 5MB)</span>
          </label>
          <label>
            Source of Funds <span class="req">*</span>
            <vs-select
              autocomplete
              v-model="sourceFunds"
              placeholder="Select source"
              required
              :danger="isSourceOfFundsInvalid"
              danger-text="Please select a source of funds."
              v-on:change="isSourceOfFundsInvalid = false">
              <vs-select-item :key="index" :value="src" :text="src.display" v-for="(src, index) of sourceFundsList" />
            </vs-select>
          </label>
          <label>
            Source of funds document (ex: bank statement)
            <input type="file" accept="image/jpeg,application/pdf,image/png" name="file" @change="onSofFileChange" />
            <span class="error" v-if="isSofDocInvalid">Please upload a valid supporting document (max 5MB)</span>
          </label>
          <label>
            Your reference ID <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on reference ID" />
              <span class="tooltip-text">
                <strong>Reference ID</strong>
                <br>
                You can provide your own reference to easily find this transaction in the future.
              </span>
            </span>
            <vs-input size="default" placeholder="Merchant ID" v-model="customExtId" />
          </label>
          <label>
            Message to Beneficiary
            <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on message to Beneficiary" />
              <span class="tooltip-text">
                <strong>Message to Beneficiary</strong>
                <br>
                This message could appear on the beneficiary's bank statements (depending on the bank).
              </span>
            </span>
            <vs-input size="default" placeholder="This will be shown to your beneficiary" v-model="remarks" counter="25" />
          </label>
          <label>
            Sender’s relationship to Beneficiary <span class="req">*</span>
            <vs-select
              autocomplete
              v-model="relationship"
              placeholder="Select relationship"
              :disabled="isSenderAlsoBeneficiary()"
              required
              :danger="isRelationshipInvalid"
              danger-text="Please select a relationship between sender and beneficiary."
              v-on:change="isRelationshipInvalid = false">
              <vs-select-item :key="index" :value="rel" :text="rel.display" v-for="(rel, index) of relationshipList" />
            </vs-select>
          </label>
          <p class="error-banner" v-if="isPurposeDocRequiredError">
            Please upload a Purpose of remittance document (ex: invoice) to proceed with this transaction.
          </p>
        </form>
      </section>
    </Transition>

    <!-- Step 3 : Confirmation -->
    <Transition name="fade">
      <section v-if="currentStepIndex === 2" class="step3-wrapper vs-con-loading__container" id="confirmation-loader">
        <dl class="payout-recap">
          <div class="account">
            <dt>From :</dt>
            <dd>{{ payout.sender_id }}</dd>
            <dd v-if="senderAccount.first_name || senderAccount.last_name">{{ `${senderAccount.first_name ? senderAccount.first_name : ''} ${senderAccount.last_name ? senderAccount.last_name : ''}` }}</dd>
          </div>
          <div>
            <dt>Deducted from balance (including fees):</dt>
            <dd>{{ payout.sending_currency }}</dd>
            <dd>{{ formatQuoteAmount(payout.balance_debit_amount, payout.sending_currency) }}</dd>
          </div>
          <div class="details-start">
            <dt>Original amount</dt>
            <dd>{{ payout.sending_currency }}</dd>
            <dd>{{ formatQuoteAmount(payout.sending_amount, payout.sending_currency) }}</dd>
          </div>
          <div>
            <dt>Transaction Fee</dt>
            <dd>{{ payout.fee_currency }}</dd>
            <dd>{{ formatQuoteAmount(payout.fee - payout.intermediary_bank_fee, payout.fee_currency) }}</dd>
          </div>
          <div v-if="payout.intermediary_bank_fee">
            <dt>Intermediary Bank Fee</dt>
            <dd>{{ payout.fee_currency }}</dd>
            <dd>{{ formatQuoteAmount(payout.intermediary_bank_fee, payout.fee_currency) }}</dd>
          </div>
          <div class="details-end">
            <dt>{{ `Conversion rate` }}</dt>
            <dd>{{ `${payout.sending_currency} 1.0000` }}</dd>
            <dd>{{ `${payout.receiving_currency} ${formatExchangeRateValue(payout.exchange_rate)}` }}</dd>
          </div>
          <div class="account">
            <dt>Beneficiary :</dt>
            <dd>{{ payout.destination_account.account.account_number }}</dd>
            <dd>{{ `${payout.destination_account.account.account_name} (${payout.destination_account.currency})` }}</dd>
          </div>
          <div>
            <dt>Receives:</dt>
            <dd>{{ payout.receiving_currency }}</dd>
            <dd>{{ formatQuoteAmount(payout.receiving_amount) }}</dd>
          </div>
          <div class="others" v-if="payout.external_id">
            <dt>External Reference:</dt>
            <dd>{{ payout.external_id }}</dd>
          </div>
          <div class="others" v-if="payout.remarks">
            <dt>Message to beneficiary :</dt>
            <dd>{{ payout.remarks }}</dd>
          </div>
        </dl>
        <div>
          <button class="btn confirm" @click="confirmPayout()">Submit</button>
          <p>Quotation expires at {{ formatDate(quote.expires_at, 'HH:mm, DD/MM/YYYY') }}</p>
          <p>This transfer will be processed after submission, and the amount will be deducted from your balance. </p>
        </div>
      </section>
    </Transition>

    <button @click.prevent="nextBtnHandler()"
      id="next-button"
      v-if="currentStepIndex < payoutCreationSteps.length - 1" class="btn next vs-con-loading__container"
      :disabled="!hasRights || isMerchantConfigLoading || (errors.length > 0 && currentStepIndex < 1)">
      {{ isButtonLoading ? '' : 'Next' }}
    </button>
    <!-- Quotation expiry -->
    <p v-if="quote.id && currentStepIndex < 2">
      Quote valid until {{ formatDate(quote.expires_at, 'HH:mm, DD/MM/YYYY') }}. <br> Please complete the transaction before then.
    </p>
    <p v-else-if="quote.id && isQuoteExpired" class="banner error mt-2">Quote expired at {{ formatDate(quote.expires_at, 'HH:mm, DD/MM/YYYY') }}. Please input new amounts to retry. <router-link to="/fiat-payout-admin">Start again</router-link></p>
    <!-- End screen - SUCCESS -->
    <Transition name="fade">
      <section v-if="currentStepIndex >= payoutCreationSteps.length && isTransferConfirmed" class="end-screen-wrapper success">
        <p class="banner">
          Transaction submitted!
        </p>

        <div>
          <div class="recap">
            <ul>
              <li>
                {{ formatDate(payout.created_at, 'DD-MM-YYYY, hh:mm') }}
                <br>
                Ref no: {{ payout.reference_number }}
              </li>
              <!-- <li>
              <button>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none">
                  <path stroke="#9E9E9E" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15.204v3.688c0 .56.21 1.096.586 1.49A1.95 1.95 0 0 0 6 21h12c.53 0 1.04-.222 1.414-.617.375-.395.586-.932.586-1.49v-3.689M12.001 3v11.943m0 0 4.572-4.564M12 14.943 7.43 10.379" />
                </svg>
              </button>
            </li> -->
            </ul>
            <dl class="payout-recap">
              <div class="account">
                <dt>From :</dt>
                <dd>{{ payout.sender_id }}</dd>
                <dd>{{ getSenderName() }}</dd>
              </div>
              <div>
                <dt>Deducted from balance (including fees):</dt>
                <dd>{{ payout.sending_currency }}</dd>
                <dd>{{ formatQuoteAmount(payout.balance_debit_amount, payout.sending_currency) }}</dd>
              </div>
              <div class="details-start">
                <dt>Original amount</dt>
                <dd>{{ payout.sending_currency }}</dd>
                <dd>{{ formatQuoteAmount(payout.sending_amount, payout.sending_currency) }}</dd>
              </div>
              <div>
                <dt>Transaction Fee</dt>
                <dd>{{ payout.fee_currency }}</dd>
                <dd>{{ formatQuoteAmount(payout.fee - payout.intermediary_bank_fee, payout.fee_currency) }}</dd>
              </div>
              <div v-if="payout.intermediary_bank_fee">
                <dt>Intermediary Bank Fee</dt>
                <dd>{{ payout.fee_currency }}</dd>
                <dd>{{ formatQuoteAmount(payout.intermediary_bank_fee, payout.fee_currency) }}</dd>
              </div>
              <div class="details-end">
                <dt>{{ `Conversion rate` }}</dt>
                <dd>{{ `${payout.sending_currency} 1.0000` }}</dd>
                <dd>{{ `${payout.receiving_currency} ${formatExchangeRateValue(payout.exchange_rate)}` }}</dd>
              </div>
              <div class="account">
                <dt>Beneficiary :</dt>
                <dd>{{ payout.destination_account.account.account_number }}</dd>
                <dd>{{ `${payout.destination_account.account.account_name} (${payout.destination_account.currency})` }}</dd>
              </div>
              <div>
                <dt>Receives:</dt>
                <dd>{{ payout.receiving_currency }}</dd>
                <dd>{{ formatQuoteAmount(payout.receiving_amount) }}</dd>
              </div>
              <div class="others" v-if="payout.external_id">
                <dt>External Reference:</dt>
                <dd>{{ payout.external_id }}</dd>
              </div>
              <div class="others" v-if="payout.remarks">
                <dt>Message to beneficiary :</dt>
                <dd>{{ payout.remarks }}</dd>
              </div>
            </dl>
          </div>

          <div>
            <p class="explanation">
              Your transfer is on its way, a confirmation will be sent to you once completed.
              You can see the current status in the <router-link to="/fiat-payout-admin">Payout Transaction History</router-link>.
            </p>
            <div class="action-group">
              <button class="btn" @click="initForm()">Create another payout</button>
              <!-- <router-link to="/fiat-payout-admin">View Payout Transaction History</router-link> -->
            </div>
          </div>
        </div>
      </section>
    </Transition>

    <!-- End screen - ERROR -->
    <Transition name="fade">
      <section v-if="currentStepIndex >= payoutCreationSteps.length && !isTransferConfirmed" class="end-screen-wrapper error">
        <p class="banner">
          Transaction is unsuccessful.
        </p>
        <p class="explanation">
          The Transfer was not successful. Please try again. If the problem persists, please contact our support team at <a href="mailto:support@triple-a.io">support@triple-a.io</a>
        </p>
        <ul>
          <li>{{ today }}</li>
          <li>Ref no: {{ payout.reference_number }} </li>
        </ul>
        <div class="action-group">
          <button class="btn" @click="restart()">Try again</button>
          <router-link to="/fiat-payout-admin">View Payout Transaction History</router-link>
        </div>
      </section>
    </Transition>

    <!-- Edit account popup -->
    <fiat-account-edit-popup
      :accountToEdit="accountToBeEdited"
      :bankDetailsToEdit="bankAccountDetails"
      :shouldShowEdit="shouldShowAccountDetails"
      :isActive="shouldShowAccountDetails"
      :shouldDisplayKycUploadButton="false"
      :countryList="countryList"
      @popup-close="handleAccountDetailsClose"
      @account-updated="handleAccountUpdate" />

    <!-- No rights text -->
    <p v-if="!hasRights">
      You do not have the rights to create a FIAT Payout with this account.
    </p>
    <!-- No default sender -->
    <p v-if="!isMerchantConfigLoading && (merchantConfig.is_restrict_to_self && !merchantConfig.restricted_sender_id)">Please create an account to be able to access the fiat payout creation feature.
      <br> To create an account, either <a href="https://triplea-technologies.stoplight.io/docs/fiat-payout-api-doc/0419f5806266e-create-an-individual" target="_blank">create an individual account</a> or a <a href="https://triplea-technologies.stoplight.io/docs/fiat-payout-api-doc/76f510af3fef1-create-a-company" target="_blank">create a company account</a>.<br>
      Need any help ? Contact our support team at <a href="mailto:support@triple-a.io">support@triple-a.io</a>
    </p>
    <!-- Loader -->
    <div class="vs-con-loading__container" id="fiat-payout-creation-loader" :enabled="isMerchantConfigLoading"></div>
  </section>

</template>

<script>
import FiatBalance from '../../components/balances/FiatBalances.vue';
import { PAYOUT_PURPOSES, SUPPORTED_CRYPTO_CURRENCIES, PAYOUT_MODES, PAYOUT_RELATIONSHIP_VALUES, FIAT_FEE_MODES, PAYOUT_SOURCE_OF_FUNDS, PAYOUT_ACCOUNT_TYPES, PAYOUT_DOC_TYPES } from '../../constants';
import FiatAccountDetails from '../../molecules/FiatAccountDetails.vue';
import FiatAccountEditPopup from '../../molecules/FiatAccountEditPopup.vue';

export default {
  components: { FiatBalance, FiatAccountDetails, FiatAccountEditPopup },
  data() {
    return {
      MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
      hasRights: true,
      isAllowedToChooseSender: false,
      merchantConfig: {
        restricted_sender_id: '',
        is_restrict_to_self: false,
        name: '',
        kyc_reliance: 'none',
        restricted_sender_type: PAYOUT_ACCOUNT_TYPES.INDIVIDUAL,
      },
      requestToken: '',
      currentStepIndex: 0,
      payoutCreationSteps: [
        'Specify Beneficiary and payout amount',
        'Purpose of payout',
        'Confirmation'
      ],
      PAYOUT_MODES: PAYOUT_MODES,

      individualAccountsList: [],
      companyAccountsList: [],

      senderAccount: null,
      sendingAmount: '',
      sendingBalance: {
        available_balance: 0,
        created_at: '',
        crypto_blockchain: null,
        currency: '',
        id: '',
        label: '',
        reserved_balance: 0,
        total_balance: 0,
        type: '',
        updated_at: ''
      },
      sendingCurrency: null,
      availableSendingCurrencies: [],

      destinationAccountsList: [],
      destinationAccount: null,

      receivingAmount: '',
      receivingCurrency: null,
      purposeList: [],
      purpose: null,
      purposeDocument: null,
      transferMode: null,
      customExtId: '',
      sourceFundsList: [],
      sourceFunds: null,
      sofDocument: null,
      relationshipList: [],
      relationship: null,
      remarks: '',

      exchangeRate: -1,
      feeAmount: 0,
      feeCurrency: '',
      quote: {
        'id': '',
        'external_id': 'string',
        'mode': '',
        'source_country': '',
        'destination_country': '',
        'destination_account_type': '',
        'sending_amount': 0.00,
        'sending_currency': '',
        'receiving_amount': 0.00,
        'receiving_currency': '',
        'exchange_rate': 1.00,
        'fee': 1.00,
        'fee_currency': ''
      },
      isQuoteLoading: false,
      shouldDisplayDetails: false,
      searchTimer: undefined,

      payout: {},

      // swift fee
      feeMode: FIAT_FEE_MODES.SHA.value,
      feeModeOptions: FIAT_FEE_MODES,
      targetedCurForSwiftFee: ['USD', 'GBP', 'EUR', 'SGD', 'USDT', 'USDC'],

      // Flags
      shouldDisplayNotification: false,
      isQuoteDetailsDisplayed: false,
      isCreateBeneficiaryFlow: false,
      isRequirementReasonDisplayed: false,
      isButtonLoading: false,
      isConfirmationLoading: false,
      isMerchantConfigLoading: false,

      // errors
      isSendingAmountInvalid: false,
      isSenderAccountInvalid: false,
      isSendingCurrencyInvalid: false,
      isPurposeInvalid: false,
      isSourceOfFundsInvalid: false,
      isRelationshipInvalid: false,
      isPurposeDocInvalid: false,
      isSofDocInvalid: false,
      isDestinationAccountInvalid: false,
      isReceivingAmountInvalid: false,
      isQuoteExpired: false,
      isInsufficientBalance: false,
      isPurposeDocRequiredError: false,

      errors: [],
      missingInfosInAccounts: {
        sender: [],
        recipient: [],
        other: []
      },

      // account edits
      editedAccount: {},
      accountToBeEdited: {},
      shouldShowAccountDetails: false,
      shouldDisplayEditMode: false,
      isAccountPopupLoading: false,
      updateErrors: [],
      bankAccountDetails: [],
      editedBankAccountDetails: [],

      countryList: [],
      isPurposeDocRequired: false
    };
  },
  mounted() {
    this.initForm();
    this.checkRights();
    this.fetchBalances();
  },
  computed: {
    activeMerchant() {
      return this.$store.getters.activeMerchantGetter;
    },

    balancesGetter() {
      return this.$store.getters.fiatPayoutBalanceListGetter;
    },
    exchangeRateGetter() {
      return this.transferMode === PAYOUT_MODES.SENDING ?
        `${this.sendingCurrency} 1 = ${this.receivingCurrency} ${this.exchangeRate}`
        : `${this.receivingCurrency} 1 = ${this.sendingCurrency} ${this.exchangeRate}`;
    },
    totalToPayGetter() {
      return this.sendingAmount + this.feeAmount;
    },
    canDecideSwiftFeeMode() {
      try {
        return this.destinationAccount.account.network.includes('network:swift') &&  this.targetedCurForSwiftFee.includes(this.sendingCurrency);
      } catch (e) {
        return false;
      }
    }
  },
  methods: {
    initForm() {
      this.resetValues();
      this.fetchMerchantConfig();
      this.fetchBalances();
      this.setDestinationAccounts();
      this.setPurposeList();
      this.setSendingCurrencies();

      this.sourceFundsList = PAYOUT_SOURCE_OF_FUNDS;
      this.relationshipList = PAYOUT_RELATIONSHIP_VALUES;
      this.feeMode = FIAT_FEE_MODES.SHA.value;
    },

    restart() {
      this.initForm();
      this.currentStepIndex = 0;
    },

    /**
     * creates a quote which will update the amounts
     */
    async createQuote() {
      clearInterval(this.searchTimer);
      this.errors = [];
      this.isQuoteExpired = false;
      if (!this.isInputValidForQuote()) {
        return;
      }
      this.isQuoteLoading = true;
      if (this.transferMode === PAYOUT_MODES.SENDING) {
        this.receivingAmount = '';
      } else {
        this.sendingAmount = '';
      }
      const accessToken = await this.getRequestToken();
      const payload = {
        token: accessToken,
        mode: this.transferMode,
        sending_currency: this.sendingCurrency,
        receiving_currency: this.receivingCurrency,
        sending_amount: this.sendingAmount,
        receiving_amount: this.receivingAmount,
        destination_country: this.destinationAccount.country_code,
        destination_account_id: this.destinationAccount.id,
      };

      if (this.canDecideSwiftFeeMode) {
        payload['charge_type'] = this.feeMode;
      }

      this.$store.dispatch('createFiatPayoutQuote', payload).then(res => {
        if (res.status === 201) {
          this.isQuoteLoading = false;
          this.quote = Object.assign(res.data);
          this.receivingAmount = this.quote.receiving_amount;
          this.sendingAmount = this.quote.sending_amount;
          this.exchangeRate = this.quote.exchange_rate;
          this.feeAmount = this.quote.fee;
          this.feeCurrency = this.quote.fee_currency;
        }
      }).catch(e => {
        this.isQuoteLoading = false;
        this.handleError(e.response);
      });
    },

    /**
     * Creating a transfer with the latest quote.
     */
    async createPayout() {
      this.errors = [];
      const accessToken = await this.getRequestToken();
      const payload = {
        token: accessToken,
        quoteId: this.quote.id,
        destinationAccountId: this.destinationAccount.id,
        purpose: this.purpose.value,
        senderId: this.senderAccount.id
      };
      // optional fields
      if (this.customExtId) {
        payload['customId'] = this.customExtId;
      }
      if (this.isSenderAlsoBeneficiary()) {
        this.relationship = PAYOUT_RELATIONSHIP_VALUES.SELF;
      }
      payload['relationship'] = this.relationship.value;
      payload['sourceOfFunds'] = this.sourceFunds.value;

      if (this.remarks) {
        payload['remarks'] = this.remarks;
      }
      // set btn to loading mode
      this.isButtonLoading = true;
      this.$store.dispatch('createFiatPayoutFromQuote', payload).then(res => {
        if (res.status === 201) {
          this.isButtonLoading = false;

          if (this.isPurposeDocRequired && !this.purposeDocument) {
            this.isPurposeDocRequiredError = true;
            return;
          } else {
            this.isPurposeDocRequiredError = false;
          }
          // display the confirmation screen
          this.payout = res.data;
          this.currentStepIndex = 2;
          if (this.senderAccount.id === this.destinationAccount.id) {
            this.relationship = PAYOUT_RELATIONSHIP_VALUES.SELF;
          }
        }
        // Add Purpose document
        if (this.purposeDocument) {
          this.$store.dispatch('uploadDocForFiatPayout', {
            token: accessToken,
            data: {
              payoutId: this.payout.id,
              file: this.purposeDocument,
              type: PAYOUT_DOC_TYPES.INVOICE.value
            }
          });
        }
        // Add Source of funds document
        if (this.sofDocument) {
          this.$store.dispatch('uploadDocForFiatPayout', {
            token: accessToken,
            data: {
              payoutId: this.payout.id,
              file: this.sofDocument,
              type: PAYOUT_DOC_TYPES.BANK_STATEMENT.value
            }
          });
        }
        // no callback needed
      }).catch(e => {
        this.isButtonLoading = false;

        if (e.response.status === 400 && e.response.data.errors && e.response.data.errors.length > 0) {
          this.missingInfosInAccounts = {
            sender: [],
            recipient: [],
            other: []
          };
          e.response.data.errors.forEach(err => {
            if (!err.errorCode) {
              // Standard message from BE is : should have required property 'address' for the Sender
              const splitted = err.message.split(' '); // we can split with space since the path never have spaces
              const path = err.path;
              const accountType = splitted.pop();
              if (!path || !accountType) {
                this.handleError(e.response);
              } else if (accountType.toLowerCase() === 'sender') {
                this.missingInfosInAccounts.sender.push(path);
              } else if (accountType.toLowerCase() === 'recipient') {
                this.missingInfosInAccounts.recipient.push(path);
              } else {
                this.missingInfosInAccounts.other.push(path);
              }
            } else {
              // 'invalid.field' || 'compliance.failed'
              this.handleError(e.response);
            }
          });
        } else {
          this.handleError(e.response);
        }
      });
    },

    async confirmPayout() {
      this.errors = [];
      const access_token = await this.getRequestToken();
      this.isConfirmationLoading = true;
      this.$store.dispatch('confirmFiatPayout', { token: access_token, payoutId: this.payout.id }).then(() => {
        this.isTransferConfirmed = true;
        this.isConfirmationLoading = false;
        this.fetchBalances();
        this.currentStepIndex = 3;
      }).catch(e => {
        // Not using handleError since we need to check for specific errors and reset a different value
        this.isConfirmationLoading = false;
        this.handleError(e.response);
      });
    },

    /**
      * Is true if the step is Complete, ie that we are redy to continue to the next step.
      * To refactor to one function per step if it becomes too long
      * @param {number} index 
      */
    isStepComplete(index) {
      let isValid = true;
      if (index === 0) {
        // first screen, where users inputs amounts and creates a quote
        const areInputsValid = this.isInputValidForQuote();
        this.checkQuoteExpiry();
        if (this.isSenderAlsoBeneficiary()) {
          this.relationship = PAYOUT_RELATIONSHIP_VALUES.SELF;
        }
        return !this.isQuoteLoading && !this.isQuoteExpired && this.quote.id && areInputsValid;
      } else if (index === 1) {
        // second screen, where users inputs purpose and other details
        this.checkQuoteExpiry();
        if (this.isQuoteExpired) {
          return false;
        }
        this.isPurposeInvalid = false;
        this.isSourceOfFundsInvalid = false;
        this.isRelationshipInvalid = false;
        if (!this.purpose) {
          isValid = false;
          this.isPurposeInvalid = true;
        }
        if (!this.sourceFunds) {
          isValid = false;
          this.isSourceOfFundsInvalid = true;
        }
        if (!this.relationship) {
          isValid = false;
          this.isRelationshipInvalid = true;
        }
        if (this.purposeDocument && this.purposeDocument.size > this.MAX_FILE_SIZE) {
          isValid = false;
          this.isPurposeDocInvalid = true;
        }
        if (this.sofDocument && this.sofDocument.size > this.MAX_FILE_SIZE) {
          isValid = false;
          this.isSofDocInvalid = true;
        }
        return isValid;
      } else if (index === 2) {
        // third screen, where users only confirm the payout
        return true;
      }
    },


    openEditSender() {
      this.shouldShowAccountDetails = true;
      this.shouldDisplayEditMode = true;
      this.updateErrors = [];
      if (this.countryList.length < 1) {
        this.fetchCountryList();
      }
      this.editedAccount = JSON.parse(JSON.stringify(this.senderAccount));
      this.accountToBeEdited = this.senderAccount;
      this.bankAccountDetails = [];
      this.editedBankAccountDetails = [];
    },

    openEditReceiver() {
      this.shouldShowAccountDetails = true;
      this.shouldDisplayEditMode = true;
      this.isAccountPopupLoading = true;
      this.updateErrors = [];
      if (this.countryList.length < 1) {
        this.fetchCountryList();
      }
      // fetching the account
      this.getRequestToken().then((token) => {
        this.$store.dispatch('fetchFiatIndividual', { token: token, id: this.destinationAccount.owner_id }).then(res => {
          if (res.status === 200) {
            if (res.data.length > 0) {
              this.editedAccount = JSON.parse(JSON.stringify(res.data));
              this.accountToBeEdited = JSON.parse(JSON.stringify(res.data));
              this.isAccountPopupLoading = false;
            }
          }
        }).catch(e => {
          if (e.response.status === 404) {
            this.$store.dispatch('fetchFiatCompany', { token: token, id: this.destinationAccount.owner_id }).then(res => {
              if (res.status === 200) {
                this.editedAccount = JSON.parse(JSON.stringify(res.data));
                this.accountToBeEdited = JSON.parse(JSON.stringify(res.data));
                this.isAccountPopupLoading = false;
              }
            }).catch(() => {
              this.editedAccount = {};
              this.accountToBeEdited = {};
              this.isAccountPopupLoading = false;
            });
          }
        });
      });

      // set bank details
      this.bankAccountDetails = [this.destinationAccount];
      this.editedBankAccountDetails = [JSON.parse(JSON.stringify(this.destinationAccount))];
    },

    /*************************** HANDLERS ****************************/
    async nextBtnHandler() {
      this.errors = [];
      const isSendingMode = this.transferMode === PAYOUT_MODES.SENDING;

      if (this.currentStepIndex === 0) {
        if (this.isStepComplete(0)) { // if in a separate if to avoid showing error on first load
          // check if sending amount is larger than balance
          if (this.sendingBalance && isSendingMode && this.sendingAmount > this.sendingBalance.available_balance) {
            this.isInsufficientBalance = true;
            this.errors.push({
              message: `Insufficient balance. Please input a smaller sending amount, or top up your balance to proceed with the transaction.`,
            });
          } else {
            this.isInsufficientBalance = false;
            this.currentStepIndex = 1;
            if (!this.isBeneficiaryIndiv()) {
              this.relationship = this.isSenderAlsoBeneficiary() ? PAYOUT_RELATIONSHIP_VALUES.SELF : PAYOUT_RELATIONSHIP_VALUES.OTHER;
            }
            // check for purpose document requirement
            this.isPurposeDocRequired = await this.isPurposeOfDocumentRequired();
          }
        }
      } else if (this.currentStepIndex === 1) {
        if (this.isStepComplete(1)) {
          this.createPayout();
        }
      }
    },

    handleAccountUpdate(updatedAccount) {
      this.isAccountPopupLoading = false;
      this.updateErrors = [];
      this.missingInfosInAccounts = {
        sender: [],
        recipient: [],
        other: []
      };
      this.accountToBeEdited = JSON.parse(JSON.stringify(updatedAccount));
    },

    handleAccountDetailsClose() {
      this.shouldShowAccountDetails = false;
    },

    handleError(e) {
      if (e.data.status === 400 && e.data.errorCode === 'expired.quote') {
        this.errors.push({
          message: `Quote expired.`,
          code: e.status
        });
        this.isTransferConfirmed = false;
        this.isQuoteExpired = true;
        this.goToStep1();
      }
      else if (e.data.status === 400 && e.data.errorCode === 'expired.transfer') {
        this.errors.push({
          message: `The rate has expired. Please retry the transfer.`,
          code: e.status
        });
        this.isTransferConfirmed = false;
        this.goToStep1();
      }
      else if (e.data.errors) {
        e.data.errors.forEach(err => {
          this.errors.push({
            message: err.message,
            code: e.status
          });
        });
      }
      else {
        this.errors.push({
          message: this.improveErrMsg(e.data),
          code: e.status
        });
      }
    },

    backButtonHandler() {
      this.resetQuoteFormErrors();
      this.currentStepIndex--;
    },

    goToStep1() {
      this.quote = {};
      this.payout = {};
      this.sendingAmount = '';
      this.receivingAmount = '';
      this.currentStepIndex = 0;
    },

    searchInputHandler(isSendingMode) {
      this.setSendingMode(isSendingMode);
      clearTimeout(this.searchTimer);
      // Code to handle the case where the user deletes the input
      if (!this.sendingAmount && isSendingMode) {
        this.quote = {};
        this.isSendingAmountInvalid = true;
        return;
      } else if (!this.receivingAmount && !isSendingMode) {
        this.quote = {};
        this.isReceivingAmountInvalid = true;
        return;

      }
      this.searchTimer = setTimeout(() => {
        this.createQuote();
      }, 500);
    },

    handleSenderChange() {
      this.resetQuoteFormErrors();
      if (this.senderAccount && !this.isKycSufficient(this.senderAccount)) {
        this.isSenderAccountInvalid = true;
      }
    },

    onPurposeFileChange(e) {
      this.isPurposeDocInvalid = false;
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) {
        this.purposeDocument = null;
        return;
      } else if (files[0].size > this.MAX_FILE_SIZE) {
        this.purposeDocument = null;
        this.isPurposeDocInvalid = true;
        return;
      }
      this.purposeDocument = files[0];
    },

    onSofFileChange(e) {
      this.isSofDocInvalid = false;
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length || files[0].size > this.MAX_FILE_SIZE) {
        this.sofDocument = null;
        return;
      }
      this.sofDocument = files[0];
    },

    /*********************** SETTERS & GETTERS ***********************/

    async getRequestToken() {
      const merchantKey = this.$store.getters.activeMerchantKeyGetter;
      const response = await this.$store.dispatch('getMerchantPaymentRequestToken', merchantKey);
      this.requestToken = response.data.access_token;
      return response.data.access_token;
    },

    async fetchBalances() {
      const token = await this.getRequestToken();
      await this.$store.dispatch('fetchFiatPayoutBalancesAction', {
        accessToken: token,
      });
    },

    fetchMerchantConfig() {
      // reset merchant config
      this.merchantConfig = {
        restricted_sender_id: '',
        is_restrict_to_self: false,
        name: '',
        kyc_reliance: 'none',
        restricted_sender_type: PAYOUT_ACCOUNT_TYPES.INDIVIDUAL,
      };
      this.errors = [];
      this.isMerchantConfigLoading = true;
      this.getRequestToken().then(token => {
        this.$store.dispatch('getMerchantFiatConfig', token).then(res => {
          this.merchantConfig = res.data;
          this.$store.commit('UPDATE_MERCHANT_FIAT_PAYOUT_CONFIG', res.data);
          this.isMerchantConfigLoading = false;
          this.isAllowedToChooseSender = !this.merchantConfig.is_restrict_to_self;
          if (this.isAllowedToChooseSender) {
            this.fetchSenders();
          } else {
            this.senderAccount = {
              'first_name': this.merchantConfig.name,
              'last_name': '',
              'id': this.merchantConfig.restricted_sender_id,
              'type': this.merchantConfig.restricted_sender_type
            };
          }
        }).catch(e => {
          this.isMerchantConfigLoading = false;
          this.handleError(e.response);
          return;
        });
      }).catch(e => {
        this.isMerchantConfigLoading = false;
        this.errors.push({
          message: `Error while fetching token ${e.response.data.message}`,
          code: e.response.status
        });
        return;
      });
    },

    /**
     * gets the sender accounts for the merchant account id
     */
    fetchSenders() {
      if (!this.isAllowedToChooseSender) {
        return;
      }
      this.individualAccountsList = [];
      this.companyAccountsList = [];
      const queryPayload = {};
      queryPayload.page = 1;
      queryPayload.per_page = 500;

      this.getRequestToken().then(token => {
        this.$store.dispatch('fetchPayoutIndividuals', { token: token }).then(response => {
          this.individualAccountsList = response.data;
        }).catch(e => {
          console.error('error while fetching sender individual accounts', e);
          return;
        });
        this.$store.dispatch('fetchPayoutCompanies', { token: token, queries: queryPayload }).then(response => {
          this.companyAccountsList = response.data;
        }).catch(e => {
          console.error('error while fetching sender corporate accounts', e);
          return;
        });
      });
    },

    /**
     * gets the destination accounts for the merchant account id
     */
    async setDestinationAccounts() {
      this.destinationAccountsList = [];
      const queryPayload = {};
      queryPayload.page = 1;
      queryPayload.per_page = 500;
      const access_token = await this.getRequestToken();
      this.$store.dispatch('fetchPayoutDestinationAccounts', { token: access_token, queries: queryPayload }).then(response => {
        this.destinationAccountsList = response.data;
      }).catch(e => {
        console.error('error while fetching destination accounts', e);
        return;
      });
    },

    setPurposeList() {
      this.purposeList = PAYOUT_PURPOSES;
    },

    /**
     * Available sending currencies = real currencies with positive amount in the merchant's balance
     */
    setSendingCurrencies() {
      try {
        const activeMerchantBalances = this.$store.getters.storedActiveMerchantGetter.balances;
        const usableCurrencies = [];
        activeMerchantBalances.forEach(bal => {
          if (bal.type === 'real' && bal.available_amount > 0) {
            usableCurrencies.push(bal.available_currency);
          }
        });
        this.availableSendingCurrencies = usableCurrencies;
        this.sendingCurrency = this.availableSendingCurrencies[0];
      } catch (e) {
        console.error(e);
        this.availableSendingCurrencies = SUPPORTED_CRYPTO_CURRENCIES; // default put all and get the error on request
      }
    },

    setSendingMode(isSending = true) {
      this.transferMode = isSending ? PAYOUT_MODES.SENDING : PAYOUT_MODES.RECEIVING;
    },

    checkRights() {
      const hasFiatPayoutRights = this.$store.getters.activeMerchantGetter.is_fiat_payout;
      const hasSenderCreationRights = (!this.merchantConfig.is_restrict_to_self && !!this.merchantConfig.restricted_sender_id) || !this.merchantConfig.is_restrict_to_self;
      this.hasRights = hasFiatPayoutRights && hasSenderCreationRights;
    },

    fetchCountryList() {
      this.getRequestToken().then(token => {
        this.$store.dispatch('getFiatCountryList', token).then(res => {
          this.countryList = res.data;
        }).catch(e => {
          console.log(e);
          return;
        });
      });
    },


    /*************************** VALIDATORS ****************************/

    isInputValidForQuote() {
      let res = true;
      this.resetQuoteFormErrors();
      const isSendingMode = this.transferMode === PAYOUT_MODES.SENDING;
      if (!this.senderAccount) {
        this.isSenderAccountInvalid = true;
        res = false;
      }

      if (this.isAllowedToChooseSender && this.senderAccount && !this.isKycSufficient(this.senderAccount)) {
        this.isSenderAccountInvalid = true;
        res = false;
      }

      if (!this.destinationAccount || !this.receivingCurrency) {
        this.isDestinationAccountInvalid = true;
        res = false;
      }

      if (!this.sendingCurrency) {
        this.isSendingCurrencyInvalid = true;
        res = false;
      }
      if (!isSendingMode && !this.receivingAmount) {
        this.isReceivingAmountInvalid = true;
        res = false;
      }
      if (!this.sendingAmount && !this.receivingAmount) {
        this.isSendingAmountInvalid = true;
        this.isReceivingAmountInvalid = true;
        res = false;
      }

      return res;
    },

    checkQuoteExpiry() {
      this.isQuoteExpired = new Date(this.quote.expires_at) < new Date();
    },

    isKycSufficient(acc) {
      return (acc.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value && acc.verification_status.toLowerCase() === 'confirmed')
        || acc.type === PAYOUT_ACCOUNT_TYPES.CORPORATE.value;
    },

    isBeneficiaryIndiv() {
      // TODO : when BE activates dynamic field depending on account type, need to have better way of checking account type
      for (let i = 0; i < this.individualAccountsList.length; i++) {
        if (this.destinationAccount.owner_id === this.individualAccountsList[i].id) {
          return true;
        }
      }
      return false;
    },

    async isPurposeOfDocumentRequired() {
      const merchantList = [
        {
          'merchant_key': 'mkey-cl47v7wb00000xv6m72j4bqnw', // Alex Staging Merchant
          // in USD
          'amount': {
            'individual': 1000,
            'company': 1000
          }
        },
        {
          'merchant_key': 'mkey-clqm57smt0000c0dj7pw7e6sa', // Aspire
          // in USD
          'amount': {
            'individual': 10000,
            'company': 15000
          }
        },
        {
          'merchant_key': 'mkey-clsczr0yw0000pqdjd276dp5e', // Aspire ACH USD
          // in USD
          'amount': {
            'individual': 10000,
            'company': 15000
          }
        }
      ];
      const activeMerchantKey = this.$store.getters.activeMerchantKeyGetter;
      const isMerchantFound = merchantList.find(merchant => merchant.merchant_key === activeMerchantKey);

      if (!isMerchantFound) {
        return false;
      }

      if (this.sendingCurrency === 'USD' || this.sendingCurrency === 'USDT') {
        if (this.sendingAmount > isMerchantFound.amount[this.senderAccount.type]) {
          return true;
        } else {
          return false;
        }
      } else {
        // need to convert to USD
        const usdEquivalent = await this.USDEquivalentAmount();
        if (usdEquivalent > isMerchantFound.amount[this.senderAccount.type]) {
          return true;
        } else {
          return false;
        }
      }
    },

    /********************* HELPERS ***********************/

    areBothIndivAccount() {
      if (this.isAllowedToChooseSender) {
        return this.senderAccount.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value && this.isBeneficiaryIndiv();
      } else {
        return this.merchantConfig.restricted_sender_type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value && this.isBeneficiaryIndiv();
      }
    },

    isSenderAlsoBeneficiary() {
      // BE checks with names, since that is how providers / banks do it
      let senderName = '';
      const benefName = this.destinationAccount.account.account_name;
      if (this.senderAccount.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value) {
        senderName = this.senderAccount.first_name + ' ' + this.senderAccount.last_name;
      } else if (this.senderAccount.type === PAYOUT_ACCOUNT_TYPES.CORPORATE.value) {
        senderName = this.senderAccount.registered_name;
      } else {
        senderName = this.merchantConfig.name;
      }
      return senderName === benefName;
    },

    formatQuoteAmount(amt, curr) {
      if (curr && this.isCryptoCurrency(curr)) {
        return this.formatCryptoAmount(amt, curr);
      } else {
        return this.formatFiatPrice(amt);
      }
    },

    formatKycStatus(status) {
      return status === 'confirmed' ? 'Confirmed' : 'Not Verified';
    },

    getSenderName() {
      if (this.senderAccount.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value && this.senderAccount.first_name && this.senderAccount.last_name) {
        return `${this.senderAccount.first_name} ${this.senderAccount.last_name}`;
      } else if (this.senderAccount.type === PAYOUT_ACCOUNT_TYPES.CORPORATE.value) {
        return this.senderAccount.registered_name;
      } else {
        return this.merchantConfig.name;
      }
    },

    improveErrMsg(error) {
      if (error.errors) {
        if (error.errors[0].errorCode === 'notfound.pricing') {
          return 'No pricing available for this currency pair. Please contact support or choose other currencies.';
        } else if (error.errors[0].errorCode) {
          const lastPath = error.errors[0].path.split('.').pop();
          return `${lastPath} ${error.errors[0].message}`;
        }
      }
      return error.message;
    },

    resetValues() {
      this.hasRights = true;
      this.isAllowedToChooseSender = false;
      this.requestToken = '';
      this.currentStepIndex = 0;

      this.individualAccountsList = [];
      this.companyAccountsList = [];
      this.senderAccount = null;
      this.sendingAmount = '';
      this.sendingBalance = {
        available_balance: 0,
        created_at: '',
        crypto_blockchain: null,
        currency: '',
        id: '',
        label: '',
        reserved_balance: 0,
        total_balance: 0,
        type: '',
        updated_at: ''
      };
      this.sendingCurrency = null;
      this.availableSendingCurrencies = [];

      this.destinationAccountsList = [];
      this.destinationAccount = null;
      this.receivingAmount = '';
      this.receivingCurrency = null;
      this.purposeList = [];
      this.purpose = null;
      this.transferMode = null;
      this.customExtId = '';
      this.sourceFundsList = [];
      this.sourceFunds = null;
      this.relationshipList = [];
      this.relationship = null;
      this.remarks = '';

      this.exchangeRate = -1;
      this.feeAmount = 0;
      this.feeCurrency = '';
      this.quote = {
        'id': '',
        'external_id': 'string',
        'mode': '',
        'source_country': '',
        'destination_country': '',
        'destination_account_type': '',
        'sending_amount': 0.00,
        'sending_currency': '',
        'receiving_amount': 0.00,
        'receiving_currency': '',
        'exchange_rate': 1.00,
        'fee': 1.00,
        'fee_currency': ''
      };
      this.isQuoteLoading = false;
      this.shouldDisplayDetails = false;
      this.searchTimer = undefined;

      this.payout = {};

        // Flags
      this.shouldDisplayNotification = false;
      this.isQuoteDetailsDisplayed = false;
      this.isCreateBeneficiaryFlow = false;
      this.isRequirementReasonDisplayed = false;
      this.isButtonLoading = false;
      this.isConfirmationLoading = false;
      this.isMerchantConfigLoading = false;

      // Errors
      this.errors = [];
      this.isSenderAccountInvalid = false;
      this.isSendingCurrencyInvalid = false;
      this.isSendingAmountInvalid = false;
      this.isPurposeInvalid = false;
      this.isPurposeDocInvalid = false;
      this.isSourceOfFundsInvalid = false;
      this.isSofDocInvalid = false;
      this.isRelationshipInvalid = false;
      this.isDestinationAccountInvalid = false;
      this.isReceivingAmountInvalid = false;
      this.isQuoteExpired = false;
      this.isPurposeDocRequiredError = false;
    },

    resetQuoteFormErrors() {
      this.isSendingAmountInvalid = false;
      this.isSenderAccountInvalid = false;
      this.isReceivingAmountInvalid = false;
      this.isDestinationAccountInvalid = false;
      this.isSendingCurrencyInvalid = false;
      this.isPurposeDocRequiredError = false;
      this.errors = [];
    },

    resetFeeMode() {
      this.feeMode = FIAT_FEE_MODES.SHA.value;
    },

    async USDEquivalentAmount() {
      const payload = {
        apiId: this.activeMerchant.accounts.find(acc => acc.crypto_currency === 'BTC').api_id, // only to fulfill the API endpoint requirement
        fiatCurrency: this.sendingCurrency
      };
      const result = await this.getExchangeRateToUSD(payload);
      const usdEquivalent = this.sendingAmount / result.data.exchange_rate;
      return usdEquivalent;
    },
  },
  watch: {
    activeMerchant() {
      this.initForm();
      this.checkRights();
      this.getRequestToken();
      this.fetchBalances();
    },
    destinationAccount(newVal) {
      if (!newVal) {
        this.receivingCurrency = '';
        return;
      }
      this.receivingCurrency = newVal.currency;
      this.resetFeeMode();
      if (this.senderAccount && (this.sendingAmount || this.receivingAmount)) {
        this.createQuote();
      }
    },
    senderAccount(newVal) {
      if (!newVal) {
        return;
      }
      if (this.destinationAccount && (this.sendingAmount || this.receivingAmount)) {
        this.createQuote();
      }
    },
    sendingBalance(newVal) {
      this.sendingCurrency = newVal.currency;
      clearTimeout(this.searchTimer);
      if (this.sendingCurrency && this.sendingAmount) {
        this.createQuote();
      }
    },
    isQuoteExpired(newVal) {
      if (newVal) {
        this.errors.push({
          message: `Quote expired. Please retry.`,
          code: '410'
        });
      }
    },
    shouldShowAccountDetails(newVal) {
      if (newVal === false) {
        this.handleAccountDetailsClose();
      }
    },
    feeMode() {
      if (this.quote.id) {
        this.createQuote();
      }
    },
    isQuoteLoading(newVal) {
      const ID = this.transferMode === PAYOUT_MODES.SENDING ? '#quote-loading-receiving' : '#quote-loading-sending';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 0.4,
          background: 'transparent'
        });
      } else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    isButtonLoading(newVal) {
      const ID = '#next-button';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 0.4,
          background: 'transparent',
          color: '#fff',
        });
      } else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    isConfirmationLoading(newVal) {
      const ID = '#confirmation-loader';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 1,
          background: '#f8f8f8',
          text: 'Confirming your transfer...',
        });
      } else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    isMerchantConfigLoading(newVal) {
      const ID = '#fiat-payout-creation-loader';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 1,
          background: '#f8f8f8',
          text: 'Fetching merchant settings...',
        });
      } else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
  },
};
</script>

<style lang="scss">
.fiat-payout-creation-wrapper {
  max-width: 1250px;
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;

  h2 {
    color: $color-text;
    font-size: 20px;
    margin-bottom: $margin-m;
  }

  button.btn {
    &.confirm {
      background-color: $color-success;

      &:hover {
        background-color: $color-success-darker;
      }

      &:disabled {
        background-color: lightgrey;
      }
    }

    &.cancel {
      background-color: $color-error;

      &:hover {
        background-color: $color-error-darker;
      }
    }

    &.back {
      margin-right: 0;
      color: $color-text;
      background: transparent;
      border: none;
      width: fit-content;
      margin-bottom: $margin-l;
      padding-left: 28px;

      &::before {
        left: 4px;
      }

      &:hover {
        text-decoration: underline;
      }
    }

    &.next {
      background-color: $color-success;
      max-width: 250px;
      margin-top: $margin-l;
      min-height: 40px;
      position: relative;

      &:hover {
        background-color: $color-success-darker;
      }

      &:disabled {
        background-color: lightgrey;
      }
    }
  }

  dl.payout-recap {
    margin-bottom: $margin-l;
    max-width: 350px;

    div {
      display: grid;
      grid-template-columns: 120px 250px;
      margin: $margin-m 0;

      dt {
        display: inline-block;
        grid-column-start: 1;
        grid-column-end: span 2;
      }

      &.account {
        display: block;

        dt {
          margin-bottom: $margin-s;
          color: $color-primary-light;
        }

        dd:last-of-type {
          font-weight: bold;
        }

        &+div {
          font-weight: bold;
        }
      }

      &.details-start {
        border-top: 1px solid lightgray;
        margin-top: $margin-m;
        padding-top: $margin-m;
      }

      &.details-end {
        margin-bottom: $margin-m;
        padding-bottom: $margin-m;
        border-bottom: 1px solid lightgray;
      }

      &.others {
        display: block;
      }
    }
  }

  section {
    margin-bottom: $margin-l;
    max-width: 1250px;

    &>form {
      display: flex;
      flex-wrap: wrap;

      label {
        position: relative;
        width: 400px;
        flex-grow: 0;
        font-size: 14px;

        div.vs-input,
        div.con-select {
          width: 100%;
        }
      }
    }

    &.step1-wrapper {
      display: flex;
      flex-wrap: wrap;

      &>.banner {
        width: 100%;
        flex-shrink: 0;
      }

      &>form {
        max-width: 550px;
        min-width: 500px;

        fieldset {
          border: none;
          width: 100%;

          legend {
            color: $color-primary;
          }

          &.money {
            display: flex;
            position: relative;
            flex-wrap: wrap;
            margin-bottom: $margin-xl;

            legend {
              color: $color-primary;
              font-weight: bold;
              position: relative;
            }

            &>div {
              &:not(.amount) {
                // currency field
                width: 120px;
                margin-right: $margin-m;
              }

              &.amount {
                flex-grow: 1;
                width: calc(100% - 120px - 12px);
              }

              &.fee-mode-wrapper {
                width: 100%;
                margin-top: $margin-l;
                position: relative;

                .tooltip {
                  position: static;
                  margin-right: $margin-m;
                }

                ul {
                  display: inline;

                  li {
                    position: relative;
                    display: inline;

                    label {
                      padding: 8px;
                      border: 1px solid lightgray;
                      color: $color-text-light;

                      &:hover {
                        cursor: pointer;
                      }
                    }

                    input {
                      display: none;
                    }

                    input:checked+label {
                      background: #c6e8fe;
                      color: $color-text;
                    }

                    &:first-of-type {
                      label {
                        border-top-left-radius: 5px;
                        border-bottom-left-radius: 5px;
                      }
                    }

                    &:last-of-type {
                      label {
                        border-top-right-radius: 5px;
                        border-bottom-right-radius: 5px;
                      }
                    }
                  }
                }
              }
            }

            small {
              width: 100%;
            }

            p {
              // total to pay
              max-width: 100%;
              margin-top: $margin-m;

            }
          }
        }

        label.collapse {
          &:hover {
            text-decoration: underline;
          }

          input {
            appearance: none;

            &::before {
              display: none;
            }

            &::after {
              content: url('../../assets/icons/expand.svg');
              transform: rotate(-90deg);
              display: inline-block;
              position: relative;
              top: 6px;
              transition: 0.3s;
            }

            &:checked::after {
              content: url('../../assets/icons/cross.svg');
              transform: rotate(360deg);
            }
          }

        }

        .account-wrapper {
          width: 100%;
          display: flex;
          flex-direction: column;
          position: relative;
          padding-bottom: $margin-l;

          &.beneficiary {
            position: relative;

            legend {
              color: $color-primary-light;
            }

            &+.money {
              margin-bottom: 0px;
            }
          }

          &>span,
          legend {
            color: $color-primary-light;
            font-weight: bold;
          }

          &>a {
            align-self: flex-end;
            color: $color-text;
            margin: 0;
            padding-bottom: 0;
            top: 60px;
            position: absolute;
            padding-top: $margin-xs;

            &:hover {
              cursor: pointer;
              text-decoration: underline;
            }
          }
        }
      }

      &>aside {
        height: 100%;
        min-width: 300px;
        padding-left: 48px;
        padding-right: 24px;
        padding-bottom: 12px;
        margin-top: 24px;
        margin-left: 48px;
        border-left: 1px solid lightgray;

        &>dl {
          div {
            display: grid;
            grid-template-columns: 100px 230px;
            padding: $margin-m;

            dt {
              grid-column-start: 1;
              grid-column-end: span 2;

              .tooltip {
                position: relative;
              }
            }

            &:first-of-type {
              border-bottom: 1px solid lightgray;
              font-weight: bold;
            }

            &:last-of-type {
              border-top: 1px solid lightgray;
              font-weight: bold;
            }

          }
        }
      }
    }

    &.step2-wrapper {
      display: flex;
      flex-direction: column;
      max-width: 850px;

      form {
        row-gap: 15px;
        justify-content: space-between;
        margin-top: $margin-l;

        input[type="file"] {
          width: 100%;

          &::file-selector-button {
            background-color: $color-primary;
            color: $color-secondary;
            border-radius: 3px;
            border: none;
            text-align: center;
            font-size: 1rem;
            padding: 12px;
            margin-right: 8px;
            transition: 0.1s ease-in-out;
            // min-height: calc(24px + 8px + 8px);

            &:hover {
              background-color: $color-primary-dark;
              cursor: pointer;
            }
          }
        }

        span.error {
          color: $color-error;
          font-size: 12px;
          margin-top: 4px;
        }
      }

      &>label {
        align-self: end;

        &:hover {
          text-decoration: underline;
        }

        input {
          appearance: none;
        }
      }
    }

    &.step3-wrapper {
      display: flex;
      flex-wrap: wrap;
      column-gap: 72px;

      &>div {
        width: 200px;
        margin-top: $margin-m;

        button {
          height: 48px;
          width: 100%;
        }

        p {
          font-size: 12px;

          &:first-of-type {
            margin-bottom: $margin-m;
          }
        }
      }
    }

    span.req {
      color: red;
      width: 1em;
    }

    &.end-screen-wrapper {
      max-width: 900px;

      .banner {
        display: block;
        position: relative;
        width: 100%;
        font-weight: bold;
        padding: 12px 12px 12px calc(48px);
        border-radius: 5px;
        color: white;
        margin-bottom: $margin-l;

        &::before {
          content: '';
          position: absolute;
          left: 12px;
          top: 12px;
        }
      }

      div.action-group {
        display: flex;
        gap: $margin-l;
        align-items: center;
        margin-top: $margin-l;

        button.btn {
          width: 200px;
          padding-top: 12px;
          padding-bottom: 12px;
        }

        a {
          &:hover {
            text-decoration: underline;
          }
        }
      }

      p.explanation {
        max-width: 750px;
        margin-bottom: $margin-m;
        font-weight: bold;

        a:hover {
          text-decoration: underline;
          cursor: pointer;
        }
      }

      &.success {
        &>.banner {
          background: $color-success;

          &::before {
            content: url('../../assets/icons/radioTicked_white.svg');
          }
        }

        &>div {
          display: flex;

          &>.recap {
            padding-right: $margin-xl;
            margin-right: $margin-xl;
            border-right: 1px solid lightgrey;

            &>ul {
              display: flex;
              justify-content: space-between;

              li {
                button {
                  height: 40px;
                  width: 40px;
                  background: transparent;
                  border: 1px solid $color-text;
                  border-radius: 5px;

                  &:hover {
                    border-color: $color-primary-light;
                    cursor: pointer;

                    svg path {
                      stroke: $color-primary-light;
                    }
                  }
                }
              }
            }
          }

          &>div:last-child {
            max-width: 550px;

            p.explanation {
              color: $color-success;

              a {
                color: $color-success;
              }
            }
          }
        }
      }

      &.error {
        &>.banner {
          background: $color-error;

          &::before {
            content: url(../../assets/icons/warning_white.svg);
            top: 10px;
          }
        }

        p.explanation {
          color: $color-error;

          a {
            color: $color-error;
          }
        }
      }
    }
  }

  .error-banner {
    width: 100%;
    background-color: #f6e2e4;
    padding: $margin-m;
    border-radius: 6px;
    margin-top: $margin-m;
    margin-bottom: $margin-m;
    font-weight: 500;
    color: darkred;
  }

  .tooltip {
    position: absolute;
    right: 0;
    border-radius: 5px;

    img {
      height: 16px;
      width: 16px;
      position: relative;
      top: 3px;
    }

    .tooltip-text {
      z-index: 1;
      visibility: hidden;
      position: absolute;
      background: white;
      padding: 10px;
      border-radius: 3px;
      font-size: 12px;
      max-width: 550px;
      min-width: 150px;
      white-space: normal;
      font-weight: normal;
      top: -16px;
      color: $color-text;
      box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;
    }

    &:hover {
      .tooltip-text {
        visibility: visible;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

#quote-loading-receiving,
#quote-loading-sending {
  height: 24px;
  width: 24px;
  position: absolute;
  right: 0px;
  top: 28px;
  background-color: white;
  margin-right: 6px;


  &>.con-vs-loading {
    height: 24px;
    width: 24px;
    position: relative;
  }
}

#fiat-payout-creation-loader {
  width: 100%;

  &[enabled] {
    height: 450px;
  }
}

.purpose-popup {
  text-align: center;

  .purpose-popup-content-wrapper.vs-popup--content {
    width: 350px;
    top: auto;
    right: auto;
    left: auto;
    bottom: auto;
  }

  .action-group {
    display: flex;
    justify-content: center;
    margin-top: $margin-m;

    button.btn {
      width: 120px;
      margin-right: 0;
    }
  }

}

.transfer-creation-popup {
  text-align: center;

  .transfer-creation-popup-content-wrapper {
    width: 350px;
    top: auto;
    right: auto;
    left: auto;
    bottom: auto;
  }
}
</style>
